import PropTypes from 'prop-types'

import TextButton from 'components/common/TextButton'
import Icon from 'components/Icon'
import theme from 'theme'
import { last } from 'utils/list'
/* Reference react-paginate */
export function BreakLink({ handleBreakClick = () => {}, index, breakLabel }) {
  return (
    <li
      css={{
        margin: '0 0.75rem',
        color: theme.colors.steel[1],
        userSelect: 'none'
      }}
    >
      <TextButton
        css={{
          margin: '0 0.5rem',
          color: theme.colors.steel[1],
          fontSize: '1rem'
        }}
        onClick={(e) => handleBreakClick(index, e)}
      >
        {breakLabel}
      </TextButton>
    </li>
  )
}

BreakLink.propTypes = {
  handleBreakClick: PropTypes.func,
  index: PropTypes.number.isRequired,
  breakLabel: PropTypes.string
}

BreakLink.defaultProps = {
  handleBreakClick: () => {},
  breakLabel: '...'
}

export function PageLink({
  index,
  selectedPage,
  handlePageSelected,
  ariaLabel
}) {
  ariaLabel = ariaLabel || `Page ${parseInt(index, 10) + 1}`

  let ariaCurrent = null
  const isActive = selectedPage === index

  if (isActive) {
    ariaCurrent = 'page'
    ariaLabel =
      ariaLabel || `Page ${parseInt(index, 10) + 1} is your current page`
  }

  return (
    <li
      css={{
        userSelect: 'none'
      }}
    >
      <TextButton
        data-test-pagination-link
        aria-current={ariaCurrent}
        aria-label={ariaLabel}
        css={{
          fontSize: '1rem',
          backgroundColor: isActive
            ? theme.colors.steel[2]
            : theme.colors.gray[1],
          margin: '0 0.25rem',
          minWidth: '2rem',
          color: isActive ? theme.colors.white[0] : theme.colors.steel[2],
          fontWeight: isActive ? theme.fontWeights[1] : null,
          borderRadius: theme.borderRadius[0],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.25rem',
          transition: 'none',

          '&:hover, &:focus': {
            color: isActive ? theme.colors.white[0] : theme.colors.black[0],
            backgroundColor: isActive
              ? theme.colors.steel[2]
              : theme.colors.gray[1]
          }
        }}
        onClick={(e) => handlePageSelected(index, e)}
      >
        {index + 1}
      </TextButton>
    </li>
  )
}

PageLink.propTypes = {
  handlePageSelected: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  selectedPage: PropTypes.number.isRequired,
  ariaLabel: PropTypes.string
}

const pageIndexAfterJump = (
  direction,
  { page, pageRangeDisplayed, pageCount }
) => {
  let jump
  if (direction === 'forward') {
    jump = page + pageRangeDisplayed
    return jump >= pageCount ? pageCount - 1 : jump
  } else {
    jump = page - pageRangeDisplayed
    return jump < 0 ? 0 : jump
  }
}

export default function Pagination({
  selectedPage,
  pageCount,
  breakLabel,
  displayCurrentPageLabel = true,
  handlePageSelected,
  pageRangeDisplayed,
  marginPagesDisplayed
}) {
  if (pageCount < 1) return null

  const handlePreviousPage = (evt) => {
    if (selectedPage > 0) {
      handlePageSelected(selectedPage - 1, evt)
    }
  }

  const handleNextPage = (evt) => {
    evt.preventDefault ? evt.preventDefault() : (evt.returnValue = false)
    if (selectedPage < pageCount - 1) {
      handlePageSelected(selectedPage + 1, evt)
    }
  }

  const handleBreakClick = (index, evt) => {
    handlePageSelected(
      pageIndexAfterJump(selectedPage < index ? 'forward' : 'backward', {
        page: selectedPage,
        pageRangeDisplayed,
        pageCount
      }),
      evt
    )
  }

  const pagination = () => {
    const items = []
    if (pageCount <= pageRangeDisplayed) {
      for (let index = 0; index < pageCount; index++) {
        items.push(
          <PageLink
            key={`page-link-${index}`}
            handlePageSelected={handlePageSelected}
            index={index}
            selectedPage={selectedPage}
          />
        )
      }
    } else {
      let leftSide = pageRangeDisplayed / 2
      let rightSide = pageRangeDisplayed - leftSide

      // If the selected page index is on the default right side of the pagination,
      // we consider that the new right side is made up of it (= only one break element).
      // If the selected page index is on the default left side of the pagination,
      // we consider that the new left side is made up of it (= only one break element).
      if (selectedPage > pageCount - pageRangeDisplayed / 2) {
        rightSide = pageCount - selectedPage
        leftSide = pageRangeDisplayed - rightSide
      } else if (selectedPage < pageRangeDisplayed / 2) {
        leftSide = selectedPage
        rightSide = pageRangeDisplayed - leftSide
      }

      let index
      let page
      let breakView
      let createPageView = (pageIndex) => (
        <PageLink
          key={`page-link-${pageIndex}`}
          handlePageSelected={handlePageSelected}
          index={pageIndex}
          selectedPage={selectedPage}
        />
      )

      for (index = 0; index < pageCount; index++) {
        page = index + 1

        // If the page index is lower than the margin defined,
        // the page has to be displayed on the left side of
        // the pagination.
        if (page <= marginPagesDisplayed) {
          items.push(createPageView(index))
          continue
        }

        // If the page index is greater than the page count
        // minus the margin defined, the page has to be
        // displayed on the right side of the pagination.
        if (page > pageCount - marginPagesDisplayed) {
          items.push(createPageView(index))
          continue
        }

        // If the page index is near the selectedPage page index
        // and inside the defined range (pageRangeDisplayed)
        // we have to display it (it will create the center
        // part of the pagination).
        if (
          index >= selectedPage - leftSide &&
          index <= selectedPage + rightSide
        ) {
          items.push(createPageView(index))
          continue
        }

        // If the page index doesn't meet any of the conditions above,
        // we check if the last item of the current "items" array
        // is a break element. If not, we add a break element, else,
        // we do nothing (because we don't want to display the page).
        if (breakLabel && last(items) !== breakView) {
          breakView = (
            <BreakLink
              key={`break-link-${index}`}
              breakLabel={breakLabel}
              handleBreakClick={handleBreakClick}
              index={index}
            />
          )
          items.push(breakView)
        }
      }
    }

    return items
  }

  const previousAriaDisabled = selectedPage === 0 ? 'true' : 'false'
  const nextAriaDisabled = selectedPage === pageCount - 1 ? 'true' : 'false'

  return (
    <section
      data-test-pagination
      css={{
        display: 'flex',
        alignItems: 'center',
        padding: '0 0.5rem',
        marginTop: theme.space[5],
        marginBottom: theme.space[3]
      }}
    >
      {displayCurrentPageLabel && (
        <label
          className="page-indication"
          css={{
            color: theme.colors.steel[1],
            flex: 1
          }}
        >
          Page {String(selectedPage + 1 || 1).toLocaleString()} of{' '}
          {String(pageCount || 1).toLocaleString()}
        </label>
      )}
      <div
        className="right"
        css={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <ul
          css={{
            display: 'flex',
            listStyleType: 'none',
            alignItems: 'center'
          }}
        >
          <li
            css={{
              userSelect: 'none',
              visibility: selectedPage === 0 ? 'hidden' : 'visible'
            }}
          >
            <TextButton
              data-test-pagination-previous
              aria-disabled={previousAriaDisabled}
              css={{
                fontSize: '1rem'
              }}
              onClick={handlePreviousPage}
            >
              <span
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: theme.space[2],
                  marginRight: theme.space[2],
                  color: theme.colors.blue[0]
                }}
              >
                <Icon
                  css={{
                    fontSize: theme.fontSizes[2],
                    display: 'block',
                    marginRight: theme.space[1]
                  }}
                  icon="chevronLeft"
                />
                Prev
              </span>
            </TextButton>
          </li>

          {pagination()}

          <li
            css={{
              userSelect: 'none',
              visibility: selectedPage === pageCount - 1 ? 'hidden' : 'visible'
            }}
          >
            <TextButton
              data-test-pagination-next
              aria-disabled={nextAriaDisabled}
              css={{
                fontSize: '1rem'
              }}
              onClick={handleNextPage}
            >
              <span
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: theme.space[2],
                  marginRight: theme.space[2],
                  color: theme.colors.blue[0]
                }}
              >
                Next
                <Icon
                  css={{
                    fontSize: theme.fontSizes[2],
                    display: 'block',
                    marginLeft: theme.space[1]
                  }}
                  icon="chevronRight"
                />
              </span>
            </TextButton>
          </li>
        </ul>
      </div>
    </section>
  )
}

Pagination.propTypes = {
  selectedPage: PropTypes.number.isRequired,
  handlePageSelected: PropTypes.func.isRequired,
  displayCurrentPageLabel: PropTypes.bool,
  pageRangeDisplayed: PropTypes.number,
  pageCount: PropTypes.number,
  marginPagesDisplayed: PropTypes.number,
  breakLabel: PropTypes.string
}

Pagination.defaultProps = {
  pageRangeDisplayed: 4,
  marginPagesDisplayed: 1,
  breakLabel: '...'
}
